import { useContext, useEffect, useState } from "react";
import { Card, CardContent } from "@/shared/ui/card";
import { Badge } from "@/shared/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/shared/ui/collapsible";
import { ChevronDown, ChevronRight } from "lucide-react";
import { cn } from "@/shared/lib/utils";
import type { NodeField } from "@/features/properties/types/propertyAttributes";
import { ExpandAllContext, HideAnsweredContext, HideRequiredContext, RequiredSetContext } from "./contexts";
import { isFieldAnswered, isFieldRequired } from "./surveyHelpers";
import { FieldRenderer } from "./FieldRenderer";

export function NodeSection({
  node,
  depth,
  propertyId,
  nodePath = [],
  onUpdateField,
  onUpdateFieldMetadata,
  onOpenPhotos,
}: {
  node: NodeField;
  depth: number;
  propertyId: string;
  /** Ids of ancestor node sections, outermost first; used for upload paths. */
  nodePath?: string[];
  onUpdateField: (refId: string, value: string | number | boolean | string[] | null) => void;
  onUpdateFieldMetadata: (
    fieldId: string,
    patch: { confidence?: number | null; evidence?: string[]; notes?: string | null }
  ) => void;
  onOpenPhotos: (urls: string[], index: number, title: string) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const expandAll = useContext(ExpandAllContext);
  const hideAnswered = useContext(HideAnsweredContext);
  const hideRequired = useContext(HideRequiredContext);
  const requiredSet = useContext(RequiredSetContext);

  // React to expand/collapse all toggle
  useEffect(() => {
    if (expandAll.version > 0) {
      setIsOpen(expandAll.expanded);
    }
  }, [expandAll.version, expandAll.expanded]);

  // Hide entire section if all children are answered
  if (hideAnswered && isFieldAnswered(node)) {
    return null;
  }

  // Hide entire section if all children are field questions
  if (hideRequired && isFieldRequired(node, requiredSet)) {
    return null;
  }

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card
        className={cn(
          "border border-border/50 overflow-hidden",
          depth === 0 ? "bg-card/50 backdrop-blur-sm" : "bg-card/30"
        )}
      >
        <CollapsibleTrigger asChild>
          <button className="w-full flex items-center justify-between p-4 hover:bg-accent/30 transition-colors text-left">
            <div className="flex items-center gap-2">
              {isOpen ? (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              )}
              <span
                className={cn(
                  "font-medium text-foreground",
                  depth === 0 ? "text-base" : "text-sm"
                )}
              >
                {node.title}
              </span>
            </div>
            <Badge variant="outline" className="text-xs text-muted-foreground">
              {node.fields.length} field{node.fields.length !== 1 ? "s" : ""}
            </Badge>
          </button>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <CardContent className="pt-0 pb-3 px-4 space-y-0.5">
            {node.fields.map((child) => (
              <FieldRenderer
                key={child.id}
                field={child}
                depth={depth + 1}
                propertyId={propertyId}
                nodePath={[...nodePath, node.id]}
                onUpdateField={onUpdateField}
                onUpdateFieldMetadata={onUpdateFieldMetadata}
                onOpenPhotos={onOpenPhotos}
              />
            ))}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}
